/**
 * @file partners.api.js
 * @module PartnersAPI
 * @description HTTP API wrapper for partners (agencies, agents) and related operations.
 * Methods return Axios promises created by axiosInstance(token).
 */

import { axiosInstance } from "./axios";

export const PartnersAPI = {

    /**
     * Create a new partner.
     * @param {Object} partners - Partner data to be created.
     * @param {string} [token=""] - Optional authorization token.
     * @returns {Promise} Axios promise.
     */
    createPartners: (partners, token = "") => {
        return axiosInstance(token).post(`/partners`, partners)
    },
    /**
     * Retrieve partners with optional pagination and search query.
     * @param {number} [limit=15] - Number of partners per page.
     * @param {number} [page=1] - Current page number.
     * @param {string} [query=""] - Optional search query.
     * @param {string} [token=""] - Optional authorization token.
     * @returns {Promise} Axios promise.
     */
    getPartners: (limit = 15, page = 1, query = "", token = "") => {
        return axiosInstance(token).get(`/partners?limit=${limit}&page=${page}${query ? `&${query}` : ""}`)
    },
    /**
     * Retrieve a partner by their ID.
     * @param {string} partnerId - ID of the partner to retrieve.
     * @param {string} [token=""] - Optional authorization token.
     * @returns {Promise} Axios promise.
     */
    getPartnerById: (partnerId, token = "") => {
        return axiosInstance(token).get(`/partners/${partnerId}`)
    },
    /**
     * Update a partner's information by their ID.
     * @param {string} partnerId - ID of the partner to update.
     * @param {Object} partner - Updated partner data.
     * @param {string} [token=""] - Optional authorization token.
     * @returns {Promise} Axios promise.
     */
    updatePartnerById: (partnerId, partner, token = "") => {
        return axiosInstance(token).put(`/partners/${partnerId}`, partner)
    },
    /**
     * Delete a partner by their ID.
     * @param {string} partnerId - ID of the partner to delete.
     * @param {string} [token=""] - Optional authorization token.
     * @returns {Promise} Axios promise.
     */
    deletePartnersById: (partnerId, token = "") => {
        return axiosInstance(token).delete(`/partners/${partnerId}`)
    },
    /**
     * Delete multiple partners by their IDs.
     * @param {Array} partners - Array of partner IDs to delete.
     * @param {string} [token=""] - Optional authorization token.
     * @returns {Promise} Axios promise.
     */
    deleteManyPartnersById: (partners, token = "") => {
        return axiosInstance(token).post(`/bulk-delete-partners`, partners)
    },
    /**
     * Retrieve available agency options.
     * @param {string} [token=""] - Optional authorization token.
     * @returns {Promise} Axios promise.
     */
    getAgenciesOptions: (token = "") => {
        return axiosInstance(token).get(`/partners-options`)
    },
    /**
     * Send an email to confirm a partner's account.
     * @param {string} partnerId - ID of the partner.
     * @param {string} email - Email address to send the confirmation to.
     * @param {string} [token=""] - Optional authorization token.
     * @returns {Promise} Axios promise.
     */
    sendEmailToConfirmAccount: (partnerId, email, token = "") => {
        return axiosInstance(token).post(`/customers/${partnerId}/confirm-account`, { email: email })
    },
    /**
     * Send an email to recover a partner's password.
     * @param {string} partnerId - ID of the partner.
     * @param {string} email - Email address to send the recovery email to.
     * @param {string} [token=""] - Optional authorization token.
     * @returns {Promise} Axios promise.
     */
    sendEmailToRecoverPassword: (partnerId, email, token = "") => {
        return axiosInstance(token).post(`/customers/${partnerId}/password-recovery`, { email: email })
    },
    /**
     * Register a partner as an agent under a specific agency.
     * @param {string} partnerId - ID of the partner to register as an agent.
     * @param {string} agencyId - ID of the agency to register the partner under.
     * @param {string} [token=""] - Optional authorization token.
     * @returns {Promise} Axios promise.
     */
    registerAsAgent: (partnerId, agencyId, token = "") => {
        return axiosInstance(token).put(`/partners/${partnerId}/register-as-agent`, { agencyToAssign: agencyId })
    },
    /**
     * Approve a partner's registration.
     * @param {string} agencyId - ID of the agency whose partner registration is to be approved.
     * @param {string} [token=""] - Optional authorization token.
     * @returns {Promise} Axios promise.
     */
    approvalRegister: (agencyId, token = "") => {
        return axiosInstance(token).put(`/partners/${agencyId}/approved`)
    },
    /**
     * Retrieve partner options.
     * @param {string} [token=""] - Optional authorization token.
     * @returns {Promise} Axios promise.
     */
    getPartnersOptions: (token = "") => {
        return axiosInstance(token).get(`/partners-options`)
    },
    /**
     * Reject a partner's registration.
     * @param {string} partnerId - ID of the partner whose registration is to be rejected.
     * @param {string} [token=""] - Optional authorization token.
     * @returns {Promise} Axios promise.
     */
    rejectRegistration: (partnerId, token = "") => {
        return axiosInstance(token).put(`/partners/${partnerId}/reject-registration`)
    },
    /**
     * Retrieve agents belonging to a specific agency.
     * @param {string} agencyId - ID of the agency.
     * @param {number} [limit=15] - Number of agents per page.
     * @param {number} [page=1] - Current page number.
     * @param {string} [query=""] - Optional search query.
     * @param {string} [token=""] - Optional authorization token.
     * @returns {Promise} Axios promise.
     */
    getAgentsByAgencyId: (agencyId, limit = 15, page = 1, query = "", token = "") => {
        return axiosInstance(token).get(`/partners/${agencyId}/agents?limit=${limit}&page=${page}${query ? `&${query}` : ""}`)
    },
    /**
     * Change a partner's ID.
     * @param {string} partnerId - Current ID of the partner.
     * @param {Object} payload - Object containing the new ID and other data.
     * @param {string} token - Authorization token.
     * @returns {Promise} Axios promise.
     */
    changePartnerId: (partnerId, payload, token = "") => {
        return axiosInstance(token).patch(`/partners/${partnerId}`, payload)
    },
    /**
     * Retrieve agent options for a specific agency.
     * @param {string} agencyId - ID of the agency.
     * @param {string} [token=""] - Optional authorization token.
     * @returns {Promise} Axios promise.
     */
    getOptionsAgentsByAgencyId: (agencyId, token = "") => {
        return axiosInstance(token).get(`/partners/${agencyId}/agents-options`)
    },
    /**
     * Export partner data.
     * @param {Object} payload - Data to be exported.
     * @param {string} query - Query parameters for the export.
     * @param {string} [token=""] - Optional authorization token.
     * @returns {Promise} Axios promise.
     */
    exportPartners: (payload, query, token = "") => {
        return axiosInstance(token).post(`/export/partners${query ? `?${query}` : ""}`, payload)
    },
    /**
     * Export agent data for a specific partner.
     * @param {string} partnerId - ID of the partner.
     * @param {Object} payload - Data to be exported.
     * @param {string} query - Query parameters for the export.
     * @param {string} [token=""] - Optional authorization token.
     * @returns {Promise} Axios promise.
     */
    exportAgentsByPartnerId: (partnerId, payload, query, token = "") => {
        return axiosInstance(token).post(`/export/partners/${partnerId}/agents${query ? `?${query}` : ""}`, payload)
    },
    /**
     * Retrieve reservations metrics.
     * @param {Object} payload - Payload containing data for metrics calculation.
     * @param {string} [token=""] - Optional authorization token.
     * @returns {Promise} Axios promise.
     */
    getReservationsMetrics: (payload, token = "") => {
        return axiosInstance(token).post(`/reservations-metrics`, payload)
    },
    /**
     * Update any attribute of a partner by their ID.
     * @param {string} partnerId - ID of the partner to update.
     * @param {Object} partner - Object containing the attributes to update.
     * @param {string} [token=""] - Optional authorization token.
     * @returns {Promise} Axios promise.
     */
    updateAnyAttributeById: (partnerId, partner, token = "") => {
        return axiosInstance(token).patch(`/partners/${partnerId}`, partner)
    },
    /**
     * Change a partner's associated agency.
     * @param {string} partnerId - ID of the partner.
     * @param {string} agencyName - New agency name to associate with the partner.
     * @param {string} [token=""] - Optional authorization token.
     * @returns {Promise} Axios promise.
     */
    changeAgentToAgency: (partnerId, agencyName, token = "") => {
        return axiosInstance(token).put(`/partners/${partnerId}/change-agency`, { agencyName: agencyName });
    },
    /**
     * Reassign a partner to a new agency.
     * @param {string} partnerId - ID of the partner to reassign.
     * @param {string} agencyId - ID of the new agency.
     * @param {string} [token=""] - Optional authorization token.
     * @returns {Promise} Axios promise.
     */
    reassignAgency: (partnerId, agencyId, token = "") => {
        return axiosInstance(token).put(`/partners/${partnerId}/reassign-agency`, { agencyId: agencyId });
    },

    /**
     * Retrieve histories for a specific customer.
     * @param {string} customerId - ID of the customer.
     * @param {number} limit - Number of histories per page.
     * @param {number} page - Current page number.
     * @param {string} token - Authorization token.
     * @returns {Promise} Axios promise.
     */
    getHistoriesByCustomerId: (customerId, limit, page, token = "") => {
        return axiosInstance(token).get(`/modules/customers/${customerId}/histories?limit=${limit}&page=${page}`)
    },
    /**
     * Update a history entry by its ID.
     * @param {string} historyId - ID of the history entry to update.
     * @param {Object} history - Updated history data.
     * @param {string} token - Authorization token.
     * @returns {Promise} Axios promise.
     */
    updateHistoryById: (historyId, history, token = "") => {
        return axiosInstance(token).put(`/histories/${historyId}`, history)
    },
}
